import { FieldErrors, ResolverResult } from 'react-hook-form';
import { t } from 'i18next';
import set from 'lodash/set';

import { getCanonicalAndErrors, terminateDataChecksWorker } from 'services/datachecks/datachecks';
import { DatacheckError, DataStores, ModalityError } from 'services/datachecks/types';
import { logError } from 'services/logging/logging';
import { getPackingListEnabled } from 'stores/packingList/packingList';
import { setIsValidating, setIsValidationComplete, setRawErrorCount } from 'stores/webedi/webedi';
import { ProcessSpecificationDTO } from 'support/types';

import { GenericError } from '../errors/errors';
import { isBigMessage } from '../messages/messages';
import {
  PackingListOriginalIndexes,
  stripDefaultTransportUnitFromValuesWithIndexes,
} from '../packingList/defaultTransportUnit';

// Timeout for datachecks in milliseconds (15 seconds)
export const DATACHECKS_TIMEOUT = 15000;

// Validation tracking
let currentValidationId = 0;
let activeProgressInterval: NodeJS.Timeout | undefined;

// The document the most recently started validation belongs to. Deliberately NOT the same thing as
// `currentValidationId`: a newer validation for the *same* document (the user editing a field, with
// reValidateMode onChange) must still deliver its results, so generation alone is the wrong test.
let currentValidationDocumentKey: string | undefined;

type ErrorPathRemapper = (path: string) => string;

type ModalityErrorMessages = {
  title: string;
  description: string;
};

type DatachecksResolverParams = {
  fieldsConfiguration: ProcessSpecificationDTO['fields'] | undefined;
  withTimeout?: boolean;
  modalityErrorMessages?: ModalityErrorMessages;
  dataStores?: DataStores;
  /**
   * Identity of the document being validated, from `buildDocumentValidationKey`. Supply it wherever
   * one mounted form can be handed a different document, so a validation that outlives its document
   * is discarded. Omitting it (or passing undefined) opts the run out entirely: it is never
   * discarded, and it never supersedes another caller's run.
   */
  documentKey?: string;
};

export const datachecksResolver = ({
  fieldsConfiguration,
  withTimeout = true,
  modalityErrorMessages,
  dataStores,
  documentKey,
}: DatachecksResolverParams) => {
  return async (rawData: Record<string, unknown>): Promise<ResolverResult> => {
    // Strip the default placeholder transport unit + unplaced items before validating, so we
    // validate exactly the payload submit sends. packingListEnabled is read from the webedi store
    // (a no-op when off), so callers don't thread it. Kept out of getCanonical/the worker on
    // purpose — that layer is shared with live UI re-derivation, which must keep the placeholder.
    const { values: data, originalIndexes } = stripDefaultTransportUnitFromValuesWithIndexes(
      rawData,
      getPackingListEnabled(),
    );
    const remapErrorPath = (path: string) => remapPackingListErrorPath(path, originalIndexes);
    if (!fieldsConfiguration) {
      return { values: data, errors: {} };
    }

    // Stamped before the big-message skip, so a skipped run still supersedes: the guard exists for
    // the slowest documents, and under `withTimeout` those are exactly the ones that skip. Only a
    // real key is stamped — an un-keyed caller (TaskForm, the Storybook document page) must not
    // leave `undefined` behind and make every in-flight guarded run read as replaced.
    if (documentKey !== undefined) {
      currentValidationDocumentKey = documentKey;
    }
    const belongsToReplacedDocument = () => documentKey !== undefined && documentKey !== currentValidationDocumentKey;

    if (withTimeout && isBigMessage(data)) {
      return skipValidation(data);
    }

    try {
      const result = await validateData(
        fieldsConfiguration,
        data,
        withTimeout,
        modalityErrorMessages,
        dataStores,
        remapErrorPath,
        belongsToReplacedDocument,
      );

      return result;
    } catch (error) {
      console.log('Error in datachecksResolver', error);
      logError(error);
      return { values: data, errors: {} };
    }
  };
};

// Helper functions
const setNestedError = (
  errors: Record<string, unknown>,
  path: string,
  errorType: string,
  errorMessage: string,
): void => {
  set(errors, path, { type: errorType, message: errorMessage });
};

// The datachecks library emits flat_dotnotation with BRACKET indexes (`line_items[1].item…`), so
// the pattern must accept both notations — a dot-only match silently skips the remap and the error
// lands on the compacted (stripped) row index instead of the real form row.
const remapPackingListErrorPath = (path: string, originalIndexes?: PackingListOriginalIndexes): string =>
  path.replace(
    /^(line_items|transport_units)(?:\.(\d+)|\[(\d+)\])(?=[.[]|$)/,
    (match, section: keyof PackingListOriginalIndexes, dotIndex, bracketIndex) => {
      const originalIndex = originalIndexes?.[section][Number(dotIndex ?? bracketIndex)];
      return originalIndex === undefined ? match : `${section}.${originalIndex}`;
    },
  );

const processDatachecksErrors = (
  errors: Record<string, unknown>,
  datachecksErrors: Array<DatacheckError>,
  remapErrorPath: ErrorPathRemapper,
): void => {
  datachecksErrors.forEach((error) => {
    if (error.failed_checks.length > 0 && 'flat_dotnotation' in error) {
      const errorType = error.is_hidden ? 'hidden' : 'external';
      setNestedError(
        errors,
        remapErrorPath(error.flat_dotnotation),
        errorType,
        `[TITLE=${error.failed_checks[0].error.title}][DESCRIPTION=${error.failed_checks[0].error.description}]`,
      );
    }
  });
};

const processModalityErrors = (
  errors: Record<string, unknown>,
  modalityErrors: Array<ModalityError>,
  remapErrorPath: ErrorPathRemapper,
  modalityErrorMessages?: ModalityErrorMessages,
): void => {
  modalityErrors.forEach((error) => {
    if (error.exists === false && error.modality === 'MANDATORY' && 'flat_dotnotation' in error) {
      const fieldName = error.field.name;
      const errorTitle =
        error.error_title ||
        modalityErrorMessages?.title ||
        t('webedi:errorMessages.required.title', { field: fieldName });
      const errorDescription =
        error.error_description ||
        modalityErrorMessages?.description ||
        t('webedi:errorMessages.required.description', { field: fieldName });
      const errorType = error.is_hidden ? 'hidden' : 'required';
      setNestedError(
        errors,
        remapErrorPath(error.flat_dotnotation),
        errorType,
        `[TITLE=${errorTitle}][DESCRIPTION=${errorDescription}]`,
      );
    }
  });
};

// Creates a promise that resolves with fallback data after a timeout
const createTimeoutPromise = (data: Record<string, unknown>) => {
  let timeoutId: NodeJS.Timeout | undefined;

  const promise = new Promise<{
    originalData: Record<string, unknown>;
    errors: { datachecks: Array<DatacheckError>; modality: Array<ModalityError> };
  }>((resolve) => {
    timeoutId = setTimeout(() => {
      logError(new GenericError('WebEDI V2: Datachecks timeout'));
      // Terminate the worker to cancel any ongoing operations
      terminateDataChecksWorker();

      resolve({
        originalData: data,
        errors: { datachecks: [], modality: [] },
      });
    }, DATACHECKS_TIMEOUT);
  });

  return { promise, timeoutId };
};

/**
 * Validates data using datachecks with a timeout fallback
 * If the datachecks operation takes longer than DATACHECKS_TIMEOUT,
 * it will terminate the worker and return fallback data
 */
const validateDataOrTimeout = async (
  fieldsConfiguration: ProcessSpecificationDTO['fields'],
  data: Record<string, unknown>,
  withTimeout: boolean,
  dataStores?: DataStores,
): Promise<{
  originalData: Record<string, unknown>;
  errors: { datachecks: Array<DatacheckError>; modality: Array<ModalityError> };
}> => {
  // Increment validation ID and store it for this validation
  currentValidationId += 1;
  const thisValidationId = currentValidationId;
  const startTime = performance.now();

  // Clear any existing progress interval
  if (activeProgressInterval) {
    clearInterval(activeProgressInterval);
  }

  // Log progress every 15 seconds
  activeProgressInterval = setInterval(() => {
    const elapsed = Math.round((performance.now() - startTime) / 1000);
    console.log(`[Datachecks] Validation still running... (${elapsed}s elapsed)`);
  }, 15000);

  const cleanup = () => {
    // Only clear if this is still the active validation
    if (thisValidationId === currentValidationId && activeProgressInterval) {
      clearInterval(activeProgressInterval);
      activeProgressInterval = undefined;
    }
  };

  const shouldLog = () => thisValidationId === currentValidationId;

  try {
    // Create a promise with timeout
    const dataCheckPromise = getCanonicalAndErrors(fieldsConfiguration, data, dataStores).then(
      ({ canonical, errors }) => ({
        originalData: canonical,
        errors,
      }),
    );

    if (!withTimeout) {
      const result = await dataCheckPromise;
      cleanup();
      if (shouldLog()) {
        const totalTime = Math.round((performance.now() - startTime) / 1000);
        console.log(`[Datachecks] Validation completed successfully in ${totalTime}s`);
      }
      return result;
    }

    // Setup a timeout promise that resolves after DATACHECKS_TIMEOUT
    const { promise: timeoutPromise, timeoutId } = createTimeoutPromise(data);

    try {
      // Race the datachecks against the timeout
      const result = await Promise.race([dataCheckPromise, timeoutPromise]);

      console.log('Datachecks validation result (or timeout fallback):', result);
      // Clear the timeout if datachecks completed first
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      cleanup();
      if (shouldLog()) {
        const totalTime = Math.round((performance.now() - startTime) / 1000);
        console.log(`[Datachecks] Validation completed in ${totalTime}s`);
      }
      return result;
    } catch (error) {
      // Clear the timeout in case of error
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      throw error;
    }
  } catch (error) {
    cleanup();
    if (shouldLog()) {
      const totalTime = Math.round((performance.now() - startTime) / 1000);
      console.error(`[Datachecks] Validation failed after ${totalTime}s`, error);
    }
    throw error;
  }
};

const validateData = async (
  fieldsConfiguration: ProcessSpecificationDTO['fields'],
  data: Record<string, unknown>,
  withTimeout = true,
  modalityErrorMessages?: ModalityErrorMessages,
  dataStores?: DataStores,
  remapErrorPath: ErrorPathRemapper = (path) => path,
  belongsToReplacedDocument: () => boolean = () => false,
): Promise<ResolverResult> => {
  setIsValidating(true);

  try {
    // Use our new function to get the result with timeout handling
    const { errors } = await validateDataOrTimeout(fieldsConfiguration, data, withTimeout, dataStores);

    // Validation cannot be cancelled, so a slow run can outlive the document it was started for.
    // Its error paths describe that document, so writing them into the form now mounted produces
    // reports against the wrong document type and partners (PORTAL-22X). Contribute nothing —
    // the same shape as a run that found no errors, since the live document has its own run.
    if (belongsToReplacedDocument()) {
      // Only `isValidating` is this run's to clear — it turned the flag on above. Completion and the
      // error count describe a *result*, and a discarded run has none, so leave them to the
      // superseding run, which always finishes (or throws into the catch) and owns them.
      setIsValidating(false);
      return { values: data, errors: {} };
    }

    const merged: Record<string, unknown> = {};

    processModalityErrors(merged, errors.modality, remapErrorPath, modalityErrorMessages);
    processDatachecksErrors(merged, errors.datachecks, remapErrorPath);

    setIsValidating(false);
    setIsValidationComplete(true);
    setRawErrorCount(Object.keys(merged).length);

    if (Object.keys(merged).length > 0) {
      return { values: {}, errors: merged as FieldErrors };
    }

    return { values: data, errors: {} };
  } catch (error) {
    setIsValidating(false);
    setIsValidationComplete(true);
    setRawErrorCount(0);
    logError(error);
    return {
      values: data,
      errors: {},
    };
  }
};

const skipValidation = (data: Record<string, unknown>) => {
  setIsValidationComplete(true);
  setRawErrorCount(0);
  return { values: data, errors: {} };
};
